import type { SmRouteWaypoint } from "../../../api/smApi";
import type { Waypoint } from "./routeTotalDistance";

export interface RoutePayloadSourcePoint {
  longitude: number;
  latitude: number;
  userpoint: boolean;
  fairway: boolean;
  tracked: boolean;
}

export function buildRoutePayloadWaypoints(
  waypoints: Waypoint[],
): SmRouteWaypoint[] {
  const result: SmRouteWaypoint[] = [];

  const pushCoord = (
    coord: [number, number],
    userpoint: boolean,
    fairway: boolean,
    tracked: boolean,
  ) => {
    const last = result[result.length - 1];
    if (
      last &&
      last.longitude === coord[0] &&
      last.latitude === coord[1] &&
      last.userpoint === userpoint &&
      last.fairway === fairway &&
      last.tracked === tracked
    ) {
      return;
    }
    result.push({
      longitude: coord[0],
      latitude: coord[1],
      userpoint,
      fairway,
      tracked,
    });
  };

  if (!waypoints.length) return result;

  // Include planner waypoints, plus intermediate passage points for dense legs
  // (auto fairway routing and recorded/tracked segments).
  for (let idx = 0; idx < waypoints.length; idx += 1) {
    const wp = waypoints[idx];
    const isUserpoint = wp.userpoint !== false;
    const isAuto = wp.routingMode === "auto";
    const isTracked = wp.tracked === true;

    if (Array.isArray(wp.passage) && idx > 0) {
      const coords: number[][] = [];
      for (const feature of wp.passage) {
        if (feature.geometry?.type !== "LineString") continue;
        const lineCoords = feature.geometry.coordinates as number[][];
        coords.push(...lineCoords);
      }

      for (let cIdx = 1; cIdx < coords.length - 1; cIdx += 1) {
        const [lng, lat] = coords[cIdx];
        if (typeof lng === "number" && typeof lat === "number") {
          // Tracked intermediates must keep tracked=true or the backend may
          // treat the route as a sparse planned route.
          pushCoord([lng, lat], false, isAuto && !isTracked, isTracked);
        }
      }
    }

    // Always include the waypoint itself.
    pushCoord(
      [wp.lngLat[0], wp.lngLat[1]],
      isUserpoint,
      isAuto,
      isTracked,
    );
  }

  return result;
}

/**
 * Prefer the original full API point list when available (exact flags/order).
 * Otherwise reconstruct dense geometry from planner waypoint passages.
 */
export function buildCloudSaveWaypoints(
  waypoints: Waypoint[],
  loadedRouteAllPoints: RoutePayloadSourcePoint[] | null | undefined,
): SmRouteWaypoint[] {
  if (loadedRouteAllPoints && loadedRouteAllPoints.length > 0) {
    return loadedRouteAllPoints.map((p, index) => ({
      order_id: index + 1,
      fairway: p.fairway,
      latitude: p.latitude,
      longitude: p.longitude,
      userpoint: p.userpoint,
      tracked: p.tracked,
    }));
  }
  return buildRoutePayloadWaypoints(waypoints);
}

/** Count coordinates stored in waypoint passage LineStrings (incl. endpoints). */
export function countPassageCoordinates(waypoints: Waypoint[]): number {
  let count = 0;
  for (const wp of waypoints) {
    if (!Array.isArray(wp.passage)) continue;
    for (const feature of wp.passage) {
      if (feature.geometry?.type !== "LineString") continue;
      count += feature.geometry.coordinates.length;
    }
  }
  return count;
}
